/**
 * Cadastro do grupo de veículos: nome, descrição e as PLACAS que fazem parte
 * (escolhidas da frota própria ativa — o modelo do caminhão não é critério), e
 * o plano preventivo do grupo, opcional.
 *
 * Salvar aplica na hora ao Plano Preventivo: placa do grupo com plano segue o
 * plano do grupo (vence o ERP; ajuste manual na placa continua valendo); placa
 * que sai volta ao ERP e ao padrão. Uma placa está em um grupo só.
 */
import { BackButton } from "../components/BackButton";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  MaintenancePlanListDto, MaintenanceGroupFleetDto, MaintenanceGroupPlanItemDto, MaintenanceTechnicalDetailDto, MaintenanceKnownComponentDto, MaintenanceVehicleGroupDetailDto,
} from "@bi/types";
import { api, ApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { useCapabilities } from "../features/maintenance/useCapabilities";
import { fmtDay } from "../features/maintenance/preventive-ui";
import { fmtNumber } from "../lib/format";
import { preventiveIconSrc } from "../features/maintenance/preventive-plate-ui";

interface Form {
  name: string;
  description: string;
  active: boolean;
  plates: string[];
  planItems: PlanItemForm[];
}
interface PlanItemForm {
  componentCode: string | null; label: string; category: string; intervalKm: number | null; intervalDays: number | null;
  warnKm?: number | null; warnDays?: number | null; details?: MaintenanceTechnicalDetailDto[] | null;
}
/** Sugestões de detalhe técnico — a estrutura é aberta, qualquer rótulo vale. */
const SUGESTOES = ["Tipo de óleo", "Viscosidade", "Quantidade", "Modelo do filtro", "Peças recomendadas", "Tempo estimado", "Procedimento", "Observações técnicas"];
const soNumero = (v: string) => (v ? Number(v.replace(/\D/g, "")) : null);
const vazio: Form = { name: "", description: "", active: true, plates: [], planItems: [] };

export function MaintenanceVehicleGroupEditorPage() {
  const { id = "novo" } = useParams();
  const novo = id === "novo";
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useCapabilities();
  const edita = can("workorder.manage");

  const grupo = useQuery({
    queryKey: ["maintenance", "vehicle-group", id],
    queryFn: () => api.get<MaintenanceVehicleGroupDetailDto>(`/maintenance/vehicle-groups/${id}`),
    enabled: !novo,
  });
  const componentes = useQuery({
    queryKey: ["maintenance", "vehicle-groups", "components"],
    queryFn: () => api.get<{ items: MaintenanceKnownComponentDto[] }>("/maintenance/vehicle-groups/components"),
    staleTime: Infinity,
  });

  const [f, setF] = useState<Form | null>(novo ? vazio : null);
  const [msg, setMsg] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [aberto, setAberto] = useState(0);
  const [editandoGrupo, setEditandoGrupo] = useState(false);

  // previsão da próxima troca por item: a placa do grupo mais próxima do vencimento
  const plano = useQuery({
    queryKey: ["maintenance", "preventive", id],
    queryFn: () => api.get<MaintenancePlanListDto>(`/maintenance/preventive?group=${id}`),
    enabled: !novo,
  });
  const previsoes = new Map<string, { plate: string; nextKm: number | null; kmRemaining: number | null; nextDate: string | null; daysRemaining: number | null; lastDate: string | null }>();
  for (const r of plano.data?.rows ?? []) {
    const atual = previsoes.get(r.componentCode);
    const falta = r.status.kmRemaining ?? r.status.daysRemaining ?? Infinity;
    const faltaAtual = atual ? (atual.kmRemaining ?? atual.daysRemaining ?? Infinity) : Infinity;
    if (!r.lastExecution && !r.status.nextKm && !r.status.nextDate) continue;
    if (!atual || falta < faltaAtual) {
      previsoes.set(r.componentCode, {
        plate: r.plate, nextKm: r.status.nextKm, kmRemaining: r.status.kmRemaining, nextDate: r.status.nextDate,
        daysRemaining: r.status.daysRemaining, lastDate: r.lastExecution?.executedAt ?? null,
      });
    }
  }

  useEffect(() => {
    const d = grupo.data;
    if (!d) return;
    setF({
      name: d.group.name, description: d.group.description ?? "", active: d.group.active,
      plates: d.vehicles.map((v) => v.plate),
      planItems: d.group.planItems.map((i: MaintenanceGroupPlanItemDto) => ({ ...i })),
    });
  }, [grupo.data]);

  const salvar = useMutation({
    mutationFn: () => (novo
      ? api.post<{ group: { id: string }; plates: number; applied: { created: number; updated: number; deactivated: number } }>("/maintenance/vehicle-groups", f)
      : api.put<{ group: { id: string }; plates: number; applied: { created: number; updated: number; deactivated: number } }>(`/maintenance/vehicle-groups/${id}`, f)),
    onSuccess: (r) => {
      setErro(null);
      setMsg(`Salvo: ${r.plates} placa(s) no grupo. Plano recalculado (${r.applied.created} criado(s), ${r.applied.updated} atualizado(s)${r.applied.deactivated ? `, ${r.applied.deactivated} desativado(s)` : ""}).`);
      qc.invalidateQueries({ queryKey: ["maintenance"] });
      if (novo) navigate(`/frota/manutencao/grupos/${r.group.id}`, { replace: true });
    },
    onError: (e) => { setMsg(null); setErro(e instanceof ApiError ? e.message : "Não foi possível salvar."); },
  });

  if (!f) return <div className="p-8 text-sm text-gray-500">Carregando grupo…</div>;
  const set = (patch: Partial<Form>) => { setMsg(null); setF({ ...f, ...patch }); };
  const problemas = [
    f.name.trim().length < 2 ? "Dê um nome ao grupo." : null,
    f.plates.length === 0 ? "Escolha as placas do grupo." : null,
    ...f.planItems.map((i) => (!i.label.trim() ? "Há item do plano sem nome." : !i.intervalKm && !i.intervalDays ? `"${i.label}": informe KM ou dias.` : null)),
  ].filter((x): x is string => !!x);

  const setItem = (k: number, patch: Partial<PlanItemForm>) =>
    set({ planItems: f.planItems.map((x, j) => (j === k ? { ...x, ...patch } : x)) });
  const atualizadoEm = (grupo.data?.group as unknown as { updatedAt?: string } | undefined)?.updatedAt;

  return (
    <div className="space-y-5 pb-24">
      <BackButton fallback="/frota/manutencao/grupos" />
      <header className="[[data-layout=mobile]_&]:hidden">
        <nav className="text-sm text-gray-500" aria-label="Você está em">
          <Link to="/frota/manutencao" className="hover:underline">Central de Manutenção</Link>
          <span className="mx-1.5">›</span>
          <Link to="/frota/manutencao/grupos" className="hover:underline">Grupos de Veículos</Link>
        </nav>
        <h2 className="mt-1 text-4xl font-bold tracking-tight text-gray-900">Grupo de Veículos</h2>
        <p className="text-gray-500">Configure os itens da manutenção preventiva e os detalhes técnicos por grupo.</p>
      </header>

      {/* Cabeçalho do grupo */}
      <section className="card flex flex-wrap items-center gap-x-8 gap-y-4 p-5">
        <img src="/manutencao-bi/g-caminhao.png" alt="" className="h-14 w-20 flex-shrink-0 object-contain" />
        <div className="min-w-[13.75rem] flex-1">
          <p className="flex flex-wrap items-center gap-2">
            <span className="text-2xl font-bold text-gray-900">{novo && !f.name ? "Novo grupo de veículos" : f.name}</span>
            <span className={`rounded-md px-2 py-0.5 text-xs font-semibold ${f.active ? "bg-tm-50 text-tm-700" : "bg-gray-100 text-gray-600"}`}>{f.active ? "Ativo" : "Inativo"}</span>
          </p>
          <p className="text-gray-500">{f.description || "Veículos do grupo e o plano preventivo aplicado a cada placa."}</p>
        </div>
        <div className="border-surface-border sm:border-l sm:pl-6">
          <p className="text-3xl font-bold leading-none text-gray-900">{f.plates.length}</p>
          <p className="text-sm text-gray-500">veículos</p>
        </div>
        <div className="border-surface-border sm:border-l sm:pl-6">
          <p className="text-3xl font-bold leading-none text-gray-900">{f.planItems.length}</p>
          <p className="text-sm text-gray-500">itens preventivos</p>
        </div>
        {atualizadoEm && (
          <div className="border-surface-border sm:border-l sm:pl-6">
            <p className="text-xs text-gray-500">Última atualização</p>
            <p className="font-semibold text-gray-900">{new Date(atualizadoEm).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short", timeZone: "America/Sao_Paulo" })}</p>
          </div>
        )}
        {edita && (
          <button className="btn-secondary border-tm-600 text-tm-700" onClick={() => setEditandoGrupo((v) => !v)}>
            ✎ {editandoGrupo ? "Fechar edição" : "Editar grupo"}
          </button>
        )}
      </section>

      {(editandoGrupo || novo) && (
        <div className="space-y-5">
          <div className="card grid gap-4 p-5">
            <label className="block">
              <span className="text-sm text-gray-600">Nome do grupo</span>
              <input className="input mt-1 w-full" value={f.name} disabled={!edita} placeholder="Ex.: Frota Transbordo" onChange={(e) => set({ name: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-sm text-gray-600">Descrição</span>
              <input className="input mt-1 w-full" value={f.description} disabled={!edita} placeholder="Ex.: Caminhões, carretas e veículos pesados da frota." onChange={(e) => set({ description: e.target.value })} />
            </label>
            {!novo && (
              <label className="inline-flex items-center gap-2 text-sm text-gray-600">
                <input type="checkbox" checked={f.active} disabled={!edita} onChange={(e) => set({ active: e.target.checked })} />
                Grupo ativo (desativado, as placas voltam ao ERP e ao plano padrão)
              </label>
            )}
          </div>
          <EscolhaDePlacas groupId={novo ? null : id} selecionadas={f.plates} edita={edita} onChange={(plates) => set({ plates })} />
        </div>
      )}

      <div className="grid gap-5 xl:grid-cols-[1fr_380px]">
        <section className="space-y-3">
          <h3 className="text-2xl font-bold text-gray-900">Itens de manutenção preventiva</h3>
          {f.planItems.map((i, k) => (
            <ItemDoGrupo
              key={`${i.componentCode ?? "novo"}-${k}`} n={k + 1} i={i} edita={edita} aberto={aberto === k}
              previsao={previsoes.get(i.componentCode ?? "")}
              onAbrir={() => setAberto(aberto === k ? -1 : k)}
              onChange={(patch) => setItem(k, patch)}
              onRemover={() => { set({ planItems: f.planItems.filter((_, j) => j !== k) }); setAberto(-1); }}
              onSalvar={() => salvar.mutate()}
              salvando={salvar.isPending} podeSalvar={problemas.length === 0}
            />
          ))}
          {f.planItems.length === 0 && (
            <p className="card p-4 text-sm text-gray-600">Este grupo ainda não tem itens preventivos. Adicione abaixo.</p>
          )}
          {edita && (
            <div className="flex flex-wrap gap-2">
              <select className="input w-80" value="" onChange={(e) => {
                const c = componentes.data?.items.find((x) => x.code === e.target.value);
                if (c && !f.planItems.some((x) => x.componentCode === c.code)) {
                  set({ planItems: [...f.planItems, { componentCode: c.code, label: c.label, category: c.category, intervalKm: c.intervalKm, intervalDays: c.intervalDays }] });
                  setAberto(f.planItems.length);
                }
              }}>
                <option value="">+ Componente conhecido…</option>
                {(componentes.data?.items ?? []).filter((c) => !f.planItems.some((x) => x.componentCode === c.code)).map((c) => (
                  <option key={c.code} value={c.code}>{c.label}</option>
                ))}
              </select>
              <button className="btn-secondary" onClick={() => {
                set({ planItems: [...f.planItems, { componentCode: null, label: "", category: "Geral", intervalKm: null, intervalDays: null }] });
                setAberto(f.planItems.length);
              }}>+ Item novo</button>
            </div>
          )}
        </section>

        <aside className="space-y-3">
          <div className="card p-5">
            <h3 className="text-xl font-bold leading-snug text-gray-900">Como funciona a preventiva no Transmasi?</h3>
            <p className="mt-1 text-sm text-gray-500">O sistema automatiza o controle da manutenção preventiva para cada veículo do grupo, seguindo uma lógica simples e eficaz.</p>
            <ol className="mt-4 space-y-3">
              {[
                ["Lê a KM atual do veículo", "O sistema identifica a KM atual de cada veículo do grupo, vinda do hodômetro ou das integrações."],
                ["Compara com a última execução", "Verifica quando foi feita a última manutenção deste item e calcula o quanto já foi rodado e o tempo decorrido."],
                ["Aplica a antecedência do alerta", "Considera o valor de alerta (ex.: 2.000 km ou 15 dias) para gerar os avisos antes do vencimento."],
                ["Gera a preventiva e os alertas", "Quando atinge o limite, o item aparece na Manutenção Preventiva e pode virar uma O.S. já preenchida."],
              ].map(([t, d], n) => (
                <li key={t} className="flex gap-3 rounded-xl border border-surface-border p-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-tm-600 text-sm font-bold text-white">{n + 1}</span>
                  <span>
                    <span className="block font-semibold text-gray-900">{t}</span>
                    <span className="block text-sm text-gray-500">{d}</span>
                  </span>
                </li>
              ))}
            </ol>
          </div>
          <div className="rounded-xl border border-tm-600/20 bg-tm-50 p-4">
            <p className="font-bold text-gray-900">Mais controle. Menos surpresas.</p>
            <p className="text-sm text-gray-600">Com a manutenção preventiva bem configurada, sua frota roda mais, custa menos e fica sempre disponível para o que realmente importa: o seu negócio.</p>
          </div>
        </aside>
      </div>

      {edita && (
        <div className="fixed bottom-0 left-0 lg:left-64 right-0 bg-white border-t border-surface-border p-3 z-30 [[data-layout=mobile]_&]:bottom-[calc(3.625rem+env(safe-area-inset-bottom))] lg:[[data-layout=mobile]_&]:bottom-0 lg:[[data-layout=mobile]_&]:left-0">
          <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-3">
            <div className="flex-1 text-sm">
              {erro ? <span className="text-danger-600">{erro}</span>
                : msg ? <span className="text-tm-700">{msg}</span>
                  : problemas.length ? <span className="text-warning-700">{problemas[0]}</span>
                    : <span className="text-gray-500">{f.plates.length} placa(s) · {f.planItems.length ? `${f.planItems.length} item(ns) no plano` : "sem plano próprio"}</span>}
            </div>
            <button className="btn-primary" disabled={problemas.length > 0 || salvar.isPending} onClick={() => salvar.mutate()}>
              {salvar.isPending ? "Salvando…" : novo ? "Criar grupo" : "Salvar grupo"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Item do plano do grupo (acordeão, como a referência) ──────────────────

const TIPOS_OLEO = ["Óleo do motor", "Óleo da caixa", "Óleo do diferencial", "Óleo do retarder", "Óleo hidráulico"];
const VISCOSIDADES = ["5W-30", "10W-40", "15W-40", "20W-50", "75W-90", "80W-90", "85W-140"];

const detalhe = (i: PlanItemForm, key: string) => i.details?.find((d) => d.key === key)?.value ?? "";
const comDetalhe = (i: PlanItemForm, key: string, label: string, value: string, unit?: string): MaintenanceTechnicalDetailDto[] => {
  const resto = (i.details ?? []).filter((d) => d.key !== key);
  return value.trim() ? [...resto, { key, label, value, ...(unit ? { unit } : {}) }] : resto;
};

function Campo({ icone, rotulo, children }: { icone: string; rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-surface-border p-3">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center text-2xl text-gray-700" aria-hidden="true">{icone}</span>
      <label className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-gray-800">{rotulo}</span>
        {children}
      </label>
    </div>
  );
}

function ItemDoGrupo({
  n, i, edita, aberto, previsao, onAbrir, onChange, onRemover, onSalvar, salvando, podeSalvar,
}: {
  n: number; i: PlanItemForm; edita: boolean; aberto: boolean;
  previsao: { plate: string; nextKm: number | null; kmRemaining: number | null; nextDate: string | null; daysRemaining: number | null; lastDate: string | null } | undefined;
  onAbrir: () => void; onChange: (p: Partial<PlanItemForm>) => void; onRemover: () => void; onSalvar: () => void;
  salvando: boolean; podeSalvar: boolean;
}) {
  const [unidadeTempo, setUnidadeTempo] = useState<"meses" | "dias">(i.intervalDays && i.intervalDays % 30 === 0 ? "meses" : "dias");
  const tempoMostrado = i.intervalDays ? (unidadeTempo === "meses" ? i.intervalDays / 30 : i.intervalDays) : "";
  const icone = preventiveIconSrc(i.label, "cartao");
  const quantidade = detalhe(i, "quantidade");
  const tempo = detalhe(i, "tempo_estimado");
  const tipo = detalhe(i, "oleo_tipo");
  const visc = detalhe(i, "oleo_viscosidade");
  const input = "input mt-1 w-full";
  return (
    <article className="card overflow-hidden">
      <button type="button" onClick={onAbrir} aria-expanded={aberto} className="flex min-h-[4.5rem] w-full items-center gap-4 p-4 text-left">
        {icone ? <img src={icone} alt="" className="h-12 w-12 shrink-0 object-contain mix-blend-multiply" /> : <span className="h-12 w-12 shrink-0" />}
        <span className="min-w-0 flex-1">
          <span className="block text-lg font-bold text-gray-900">{n}. {i.label || "Novo item"}</span>
          <span className="block text-sm text-gray-500">{i.category || "Geral"}</span>
        </span>
        <span className="rounded-md bg-tm-50 px-2.5 py-0.5 text-xs font-semibold text-tm-700">Ativo</span>
        <span className="text-gray-500" aria-hidden="true">{aberto ? "⌃" : "⌄"}</span>
      </button>

      {aberto && (
        <div className="space-y-3 border-t border-surface-border p-4">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-sm font-semibold text-gray-800">Nome do item</span>
              <input className={input} disabled={!edita} value={i.label} placeholder="Ex.: Troca de óleo do motor" onChange={(e) => onChange({ label: e.target.value })} />
            </label>
            <label className="block">
              <span className="text-sm font-semibold text-gray-800">Categoria</span>
              <input className={input} disabled={!edita} value={i.category} placeholder="Ex.: Motor / Lubrificação" onChange={(e) => onChange({ category: e.target.value })} />
            </label>
          </div>

          <div className="grid gap-3 lg:grid-cols-3">
            <Campo icone="⛰" rotulo="Intervalo por KM">
              <span className="flex items-center gap-2">
                <input className={input} inputMode="numeric" disabled={!edita} value={i.intervalKm ?? ""} onChange={(e) => onChange({ intervalKm: soNumero(e.target.value) })} />
                <span className="text-sm text-gray-500">km</span>
              </span>
            </Campo>
            <Campo icone="📅" rotulo="Intervalo por tempo">
              <span className="flex items-center gap-2">
                <input
                  className={input} inputMode="numeric" disabled={!edita} value={tempoMostrado}
                  onChange={(e) => { const v = soNumero(e.target.value); onChange({ intervalDays: v == null ? null : unidadeTempo === "meses" ? v * 30 : v }); }}
                />
                <select className="input mt-1 w-28" disabled={!edita} value={unidadeTempo} onChange={(e) => setUnidadeTempo(e.target.value as "meses" | "dias")}>
                  <option value="meses">meses</option>
                  <option value="dias">dias</option>
                </select>
              </span>
            </Campo>
            <Campo icone="🔔" rotulo="Alerta antecipado">
              <span className="flex items-center gap-2">
                <input className={input} inputMode="numeric" disabled={!edita} placeholder="padrão" value={i.warnKm ?? ""} onChange={(e) => onChange({ warnKm: soNumero(e.target.value) })} />
                <span className="text-sm text-gray-500">km</span>
              </span>
              <span className="mt-1 flex items-center gap-2 text-xs text-gray-500">
                ou
                <input className="input w-16 py-1" inputMode="numeric" disabled={!edita} placeholder="15" value={i.warnDays ?? ""} onChange={(e) => onChange({ warnDays: soNumero(e.target.value) })} />
                dias (padrão 15)
              </span>
            </Campo>

            <Campo icone="💧" rotulo="Tipo de óleo">
              <select className={input} disabled={!edita} value={tipo} onChange={(e) => onChange({ details: comDetalhe(i, "oleo_tipo", "Tipo de óleo", e.target.value) })}>
                <option value="">—</option>
                {[...new Set([...TIPOS_OLEO, ...(tipo ? [tipo] : [])])].map((t) => <option key={t}>{t}</option>)}
              </select>
            </Campo>
            <Campo icone="⚙" rotulo="Viscosidade">
              <select className={input} disabled={!edita} value={visc} onChange={(e) => onChange({ details: comDetalhe(i, "oleo_viscosidade", "Viscosidade", e.target.value) })}>
                <option value="">—</option>
                {[...new Set([...VISCOSIDADES, ...(visc ? [visc] : [])])].map((t) => <option key={t}>{t}</option>)}
              </select>
            </Campo>
            <Campo icone="⚗" rotulo="Quantidade (litros)">
              <span className="flex items-center gap-2">
                <input className={input} inputMode="decimal" disabled={!edita} value={quantidade} onChange={(e) => onChange({ details: comDetalhe(i, "quantidade", "Quantidade", e.target.value, "L") })} />
                <span className="text-sm text-gray-500">L</span>
              </span>
            </Campo>

            <Campo icone="📦" rotulo="Peças previstas">
              <input className={input} disabled={!edita} placeholder="Ex.: Filtro de óleo, anel de vedação" value={detalhe(i, "pecas")}
                onChange={(e) => onChange({ details: comDetalhe(i, "pecas", "Peças previstas", e.target.value) })} />
            </Campo>
            <Campo icone="⏱" rotulo="Tempo estimado">
              <span className="flex items-center gap-2">
                <input className={input} inputMode="decimal" disabled={!edita} value={tempo} onChange={(e) => onChange({ details: comDetalhe(i, "tempo_estimado", "Tempo estimado", e.target.value, "h") })} />
                <span className="text-sm text-gray-500">horas</span>
              </span>
            </Campo>
            <Campo icone="📝" rotulo="Procedimento / observações">
              <input className={input} disabled={!edita} value={detalhe(i, "procedimento")}
                onChange={(e) => onChange({ details: comDetalhe(i, "procedimento", "Procedimento", e.target.value) })} />
            </Campo>
          </div>

          <div className="flex flex-wrap items-center gap-x-8 gap-y-2 rounded-xl border border-surface-border bg-surface-muted/60 p-4">
            <div className="min-w-[13.75rem] flex-1">
              <p className="font-bold text-gray-900">Próxima troca (previsão)</p>
              <p className="text-sm text-gray-500">
                {previsao
                  ? `Placa mais próxima do vencimento: ${previsao.plate}${previsao.lastDate ? ` · última execução em ${fmtDay(previsao.lastDate)}` : ""}.`
                  : "Sem execução registrada nas placas do grupo. A previsão aparece depois da primeira."}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500">Por KM</p>
              <p className="text-xl font-bold text-gray-900">{previsao?.nextKm != null ? `${fmtNumber(previsao.nextKm)} km` : "—"}</p>
              {previsao?.kmRemaining != null && <p className="text-xs text-gray-500">(faltam {fmtNumber(Math.max(0, previsao.kmRemaining))} km)</p>}
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500">Por data</p>
              <p className="text-xl font-bold text-gray-900">{previsao?.nextDate ? fmtDay(previsao.nextDate) : "—"}</p>
              {previsao?.daysRemaining != null && <p className="text-xs text-gray-500">(em {previsao.daysRemaining} dias)</p>}
            </div>
          </div>

          {edita && (
            <div className="flex flex-wrap justify-end gap-3">
              <button className="btn-secondary border-danger-600/40 text-danger-600" onClick={onRemover}>🗑 Desativar item</button>
              <button className="btn-primary" disabled={!podeSalvar || salvando} onClick={onSalvar}>{salvando ? "Salvando…" : "💾 Salvar alterações"}</button>
            </div>
          )}
        </div>
      )}
    </article>
  );
}

function EscolhaDePlacas({ groupId, selecionadas, edita, onChange }: {
  groupId: string | null;
  selecionadas: string[];
  edita: boolean;
  onChange: (plates: string[]) => void;
}) {
  const [busca, setBusca] = useState("");
  const [marca, setMarca] = useState("");
  const [tipo, setTipo] = useState<"" | "tractor" | "trailer">("");
  const [mostrar, setMostrar] = useState<"disponiveis" | "escolhidas">(selecionadas.length ? "escolhidas" : "disponiveis");

  const frota = useQuery({
    queryKey: ["maintenance", "vehicle-groups", "fleet"],
    queryFn: () => api.get<MaintenanceGroupFleetDto>("/maintenance/vehicle-groups/fleet"),
  });
  const marcadas = new Set(selecionadas);
  const todas = frota.data?.vehicles ?? [];
  const marcas = [...new Set(todas.map((v) => v.brand).filter((b): b is string => !!b))].sort();
  const termo = busca.trim().toUpperCase();
  const lista = todas.filter((v) =>
    (!v.group || v.group.id === groupId || marcadas.has(v.plate))
    && (mostrar !== "escolhidas" || marcadas.has(v.plate))
    && (!tipo || v.isTrailer === (tipo === "trailer"))
    && (!marca || v.brand === marca)
    && (!termo || `${v.plate} ${v.brand ?? ""} ${v.model ?? ""} ${v.year ?? ""}`.toUpperCase().includes(termo)),
  );
  const alternar = (plate: string) => {
    if (!edita) return;
    onChange(marcadas.has(plate) ? selecionadas.filter((p) => p !== plate) : [...selecionadas, plate]);
  };
  const todasListadas = lista.length > 0 && lista.every((v) => marcadas.has(v.plate));

  return (
    <section className="card p-5 space-y-3">
      <div>
        <h3 className="font-semibold text-gray-900">Veículos do grupo <span className="text-sm font-normal text-gray-500">({selecionadas.length} placa(s))</span></h3>
        <p className="text-sm text-gray-500">Escolha as placas da frota própria ativa que ainda não estão em nenhum grupo. Uma placa está em um grupo só: para movê-la, tire-a do grupo atual primeiro.</p>
      </div>
      {selecionadas.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {[...selecionadas].sort().map((p) => (
            <span key={p} className="inline-flex items-center gap-1 rounded border border-surface-border bg-surface-muted px-2 py-0.5 font-mono text-xs">
              {p}
              {edita && <button className="text-gray-400 hover:text-danger-600" aria-label={`Tirar ${p}`} onClick={() => alternar(p)}>×</button>}
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <input className="input w-56" placeholder="Buscar placa, marca, modelo, ano" value={busca} onChange={(e) => setBusca(e.target.value)} />
        <select className="input w-44" value={marca} onChange={(e) => setMarca(e.target.value)}>
          <option value="">Toda marca</option>
          {marcas.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select className="input w-40" value={tipo} onChange={(e) => setTipo(e.target.value as typeof tipo)}>
          <option value="">Cavalos e carretas</option>
          <option value="tractor">Só cavalos</option>
          <option value="trailer">Só carretas</option>
        </select>
        <select className="input w-44" value={mostrar} onChange={(e) => setMostrar(e.target.value as typeof mostrar)}>
          <option value="disponiveis">Sem grupo</option>
          <option value="escolhidas">Só as escolhidas</option>
        </select>
      </div>
      <div className="max-h-96 overflow-y-auto border border-surface-border rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-surface-muted text-xs text-gray-500 sticky top-0">
            <tr>
              <th className="px-3 py-2 w-8">
                {edita && (
                  <input
                    type="checkbox"
                    aria-label="Marcar todas as listadas"
                    checked={todasListadas}
                    onChange={(e) => {
                      const listadas = lista.map((v) => v.plate);
                      onChange(e.target.checked ? [...new Set([...selecionadas, ...listadas])] : selecionadas.filter((p) => !listadas.includes(p)));
                    }}
                  />
                )}
              </th>
              <th className="text-left px-3 py-2">Placa</th>
              <th className="text-left px-3 py-2">Marca</th>
              <th className="text-left px-3 py-2">Modelo (ERP)</th>
              <th className="text-left px-3 py-2">Ano</th>
              <th className="text-left px-3 py-2">Grupo atual</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {frota.isLoading && <tr><td colSpan={6} className="px-3 py-4 text-gray-500">Carregando frota…</td></tr>}
            {lista.map((v) => {
              const deOutro = v.group && v.group.id !== groupId ? v.group.name : null;
              return (
                <tr key={v.plate} className={`${edita ? "cursor-pointer hover:bg-surface-muted" : ""} ${marcadas.has(v.plate) ? "bg-tm-50/50" : ""}`} onClick={() => alternar(v.plate)}>
                  <td className="px-3 py-1.5">
                    <input type="checkbox" disabled={!edita} checked={marcadas.has(v.plate)} onChange={() => alternar(v.plate)} onClick={(e) => e.stopPropagation()} />
                  </td>
                  <td className="px-3 py-1.5 font-mono">{v.plate}<span className="ml-1 text-xs text-gray-400">{v.isTrailer ? "carreta" : "cavalo"}</span></td>
                  <td className="px-3 py-1.5">{v.brand ?? "—"}</td>
                  <td className="px-3 py-1.5 text-gray-600">{v.model ?? "—"}</td>
                  <td className="px-3 py-1.5">{v.year ?? "—"}</td>
                  <td className="px-3 py-1.5 text-gray-600">
                    {deOutro ? <span className={marcadas.has(v.plate) ? "text-warning-700" : ""}>{deOutro}{marcadas.has(v.plate) ? " → muda para este" : ""}</span> : v.group ? "este grupo" : "—"}
                  </td>
                </tr>
              );
            })}
            {!frota.isLoading && lista.length === 0 && <tr><td colSpan={6} className="px-3 py-4 text-gray-500">Nenhuma placa com estes filtros.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400">{lista.length} placa(s) listada(s) · {selecionadas.length} escolhida(s)</p>
    </section>
  );
}
